import { useCallback, useEffect, useState } from "react";
import fetchWithCredentials, {
  FetchWithCredentialsError,
} from "../apiClient";

type FeedbackVote = "up" | "down" | null;

export function useMessageFeedback(
  messageId: number,
  initialFeedback?: "up" | "down" | null,
) {
  const [feedback, setFeedback] = useState<FeedbackVote>(
    initialFeedback ?? null,
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setFeedback(initialFeedback ?? null);
  }, [initialFeedback, messageId]);

  const postFeedback = useCallback(
    async (body: { vote: "up" | "down" | null; reason?: string }) => {
      const data = await fetchWithCredentials(
        `/manager-api/messages/${messageId}/feedback/`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
      );
      setFeedback((data?.vote as FeedbackVote) ?? null);
    },
    [messageId],
  );

  const submitFeedback = useCallback(
    async (vote: "up" | "down", reason?: string) => {
      setSubmitting(true);
      setError(null);
      try {
        const body: { vote: "up" | "down"; reason?: string } = { vote };
        if (vote === "down" && reason) {
          body.reason = reason;
        }
        await postFeedback(body);
      } catch (err) {
        const message =
          err instanceof FetchWithCredentialsError
            ? err.message
            : "Failed to submit feedback";
        setError(message);
        throw err;
      } finally {
        setSubmitting(false);
      }
    },
    [postFeedback],
  );

  const clearFeedback = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    try {
      await postFeedback({ vote: null });
    } catch (err) {
      const message =
        err instanceof FetchWithCredentialsError
          ? err.message
          : "Failed to clear feedback";
      setError(message);
      throw err;
    } finally {
      setSubmitting(false);
    }
  }, [postFeedback]);

  return { feedback, submitFeedback, clearFeedback, submitting, error };
}
